import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_REFRESH_URL = `${API}/auth/refresh`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
  const [loading, setLoading] = useState(true);
  const refreshPromiseRef = useRef(null);

  const refreshToken = useCallback(async () => {
    if (!refreshPromiseRef.current) {
      refreshPromiseRef.current = axios
        .post(AUTH_REFRESH_URL, {}, { withCredentials: true, _skipAuthRefresh: true })
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          refreshPromiseRef.current = null;
        });
    }

    return refreshPromiseRef.current;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, _skipAuthRefresh: true });
      setUser(data);
      return true;
    } catch (e) {
      if (e.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) {
          try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true, _skipAuthRefresh: true });
            setUser(data);
            return true;
          } catch (e2) {
            setUser(false);
            return false;
          }
        }
      }

      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [refreshToken]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const requestUrl = originalRequest.url || '';
        const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register') || requestUrl.includes('/auth/logout') || requestUrl.includes('/auth/me') || requestUrl.includes('/auth/refresh');

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !originalRequest._skipAuthRefresh &&
          !isAuthRequest
        ) {
          originalRequest._retry = true;
          const refreshed = await refreshToken();
          if (refreshed) {
            return axios(originalRequest);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [refreshToken]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true, _skipAuthRefresh: true });
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true, _skipAuthRefresh: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, _skipAuthRefresh: true });
    setUser(false);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
